import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lock, Search } from 'lucide-react';

/**
 * @typedef {{ id: string, name: string, description: string, icon: import('lucide-react').LucideIcon }} SettingsItem
 * @typedef {{ id: string, label: string, items: SettingsItem[], notice?: string }} SettingsSection
 */

// L3: frozen so consumers can't accidentally mutate the shared default reference
const EMPTY_SECTIONS = Object.freeze([]);
const EMPTY_LABELS = Object.freeze({});

// js-hoist-regexp: static escape pattern hoisted to avoid recreating on every HighlightMatch call
const REGEXP_SPECIAL = /[.*+?^${}()|[\]\\]/g;

// M1: no useMemo — the split + RegExp is cheap; memo bookkeeping costs more.
// q is hoisted outside the map to avoid recomputing toLowerCase per part.
function HighlightMatch({ text, query }) {
  if (!query || !text) return text;
  const escaped = query.replace(REGEXP_SPECIAL, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  if (parts.length <= 1) return text;
  const q = query.toLowerCase();
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === q ? (
          <mark
            key={i}
            className="rounded bg-violet-100 not-italic text-violet-900 dark:bg-violet-900/40 dark:text-violet-200"
          >
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

/**
 * Settings navigation panel — renders as an absolute panel relative to a
 * `position:relative` ancestor. The gear icon trigger lives in the Topbar.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {(itemId: string) => void} [props.onNavigate]
 * @param {SettingsSection[]} [props.sections=[]] - Sections to render. Filter by role before passing.
 * @param {string} [props.maxHeight='90vh'] - Any CSS unit (px, rem, vh, %).
 * @param {Record<string, string>} [props.labels]
 * @param {string} [props.className]
 */
const TopbarSettingsMenu = ({
  isOpen,
  onClose,
  onNavigate,
  sections = EMPTY_SECTIONS,
  maxHeight = '90vh',
  labels = EMPTY_LABELS,
  className = '',
}) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  // M2: track whether the last activeIndex change was keyboard-driven
  const lastNavWasKeyboard = useRef(false);
  // H1: single ref, assigned once per render AFTER flatItems is available (below useMemo)
  const stateRef = useRef(null);

  // Auto-focus on open
  useEffect(() => {
    if (!isOpen) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  // Reset state when the menu closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [isOpen]);

  // Filter by search query + assign stable flat indices
  // js-flatmap-filter: flatMap avoids the intermediate null-filled array from map+filter(Boolean)
  const { visibleSections, flatItems } = useMemo(() => {
    let idx = 0;
    const filtered = sections.flatMap((section) => {
      if (section.notice && (!section.items || section.items.length === 0)) {
        return query ? [] : [{ ...section, items: [] }];
      }
      let items = section.items ?? [];
      if (query) {
        const lower = query.toLowerCase();
        items = items.filter(
          (item) =>
            item.name.toLowerCase().includes(lower) ||
            item.description?.toLowerCase().includes(lower),
        );
      }
      if (!items.length && !section.notice) return [];
      const indexedItems = items.map((item) => ({ ...item, flatIdx: idx++ }));
      return [{ ...section, items: indexedItems }];
    });

    return {
      visibleSections: filtered,
      flatItems: filtered.flatMap((s) => s.items),
    };
  }, [sections, query]);

  // H1: one consolidated assignment after flatItems is computed — no two-phase augmentation
  stateRef.current = { query, activeIndex, onClose, onNavigate, items: flatItems, len: flatItems.length };

  // M2: scroll only when the change came from keyboard, not from hover
  useEffect(() => {
    if (!lastNavWasKeyboard.current) return;
    panelRef.current
      ?.querySelector(`[data-settings-idx="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Click outside to close
  const handleBackdropMouseDown = useCallback(
    (e) => { if (!panelRef.current?.contains(e.target)) onClose?.(); },
    [onClose],
  );
  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('mousedown', handleBackdropMouseDown);
    return () => document.removeEventListener('mousedown', handleBackdropMouseDown);
  }, [isOpen, handleBackdropMouseDown]);

  // Keyboard nav — capture phase, reads all volatile values from stateRef
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      const { query, activeIndex, onClose, onNavigate, items, len } = stateRef.current;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // H2: reset activeIndex inline instead of via a separate effect
        if (query) { setQuery(''); setActiveIndex(0); }
        else onClose?.();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        lastNavWasKeyboard.current = true;
        setActiveIndex((i) => Math.min(i + 1, len - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        lastNavWasKeyboard.current = true;
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        // H1: consistent single source of truth — read activeIndex from the same ref
        const item = items?.[activeIndex];
        if (item) { onNavigate?.(item.id); onClose?.(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  if (!isOpen) return null;

  const noResults = flatItems.length === 0 && Boolean(query);

  // L2: hoist repeated label fallbacks
  const title = labels.title ?? 'Settings';
  const searchPlaceholder = labels.searchPlaceholder ?? 'Search settings...';

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={title}
      style={{ maxHeight }}
      className={[
        'absolute right-0 top-full z-50 mt-2',
        'flex w-[360px] flex-col overflow-hidden',
        'rounded-xl border border-[#E5E7EB] bg-white',
        'shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)]',
        'dark:border-white/10 dark:bg-[#1a2030]',
        className,
      ].filter(Boolean).join(' ')}
    >
      {/* ── Pinned header ── */}
      <div className="shrink-0 px-4 pb-3 pt-4">
        <h2 className="mb-3 text-sm font-semibold text-[#111827] dark:text-gray-100">
          {title}
        </h2>
        <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-400/20 dark:border-white/10 dark:bg-white/5 dark:focus-within:border-violet-500/60">
          <Search className="h-3.5 w-3.5 shrink-0 text-[#9CA3AF] dark:text-gray-500" strokeWidth={2} aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              // H2: reset highlight synchronously — no separate effect needed
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder={searchPlaceholder}
            autoComplete="off"
            spellCheck={false}
            aria-label={searchPlaceholder}
            className="min-w-0 flex-1 border-0 bg-transparent py-0 text-sm text-[#111827] outline-none placeholder:text-[#9CA3AF] dark:text-gray-100 dark:placeholder:text-gray-500"
          />
          <kbd className="inline-flex h-5 items-center rounded border border-[#E5E7EB] px-1.5 font-mono text-[10px] text-[#9CA3AF] dark:border-white/10 dark:text-gray-500" aria-hidden>
            esc
          </kbd>
        </div>
      </div>

      <div className="h-px shrink-0 bg-[#F3F4F6] dark:bg-white/10" role="separator" />

      {/* ── Scrollable body ── */}
      <div
        role="listbox"
        aria-label={title}
        className="scrollbar-aimsos-auto-sm min-h-0 flex-1 overflow-y-auto p-2"
      >
        {noResults ? (
          <p className="px-3 py-8 text-center text-sm text-[#6B7280] dark:text-gray-400">
            {labels.noResults
              ? `${labels.noResults} "${query}"`
              : `No settings match "${query}"`}
          </p>
        ) : (
          visibleSections.map((section) => (
            <div key={section.id} className="mb-1 last:mb-0">
              <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9CA3AF] dark:text-gray-500">
                {section.label}
              </p>

              {section.notice && section.items.length === 0 ? (
                <div className="flex items-start gap-2.5 rounded-lg px-2 py-2.5">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[#9CA3AF] dark:text-gray-500" strokeWidth={1.5} aria-hidden />
                  <p className="text-xs leading-5 text-[#9CA3AF] dark:text-gray-500">
                    {section.notice}
                  </p>
                </div>
              ) : (
                section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.flatIdx === activeIndex;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      data-settings-idx={item.flatIdx}
                      onClick={() => { onNavigate?.(item.id); onClose?.(); }}
                      onMouseEnter={() => {
                        // M2: hover does not trigger scrollIntoView
                        lastNavWasKeyboard.current = false;
                        setActiveIndex(item.flatIdx);
                      }}
                      className={[
                        'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors duration-100',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-500/30',
                        isActive
                          ? 'bg-[#F3F4F6] dark:bg-white/[0.07]'
                          : 'hover:bg-[#F3F4F6] dark:hover:bg-white/[0.07]',
                      ].join(' ')}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#6B7280] dark:text-gray-400">
                        {Icon && <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium leading-5 text-[#111827] dark:text-gray-100">
                          <HighlightMatch text={item.name} query={query} />
                        </p>
                        {item.description && (
                          <p className="text-xs leading-4 text-[#6B7280] dark:text-gray-400">
                            <HighlightMatch text={item.description} query={query} />
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TopbarSettingsMenu;
